import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { debounce } from "lodash"; // Ensure lodash is installed
import LoginService from "../../Services/login_service";
import RfqService from "../../Services/rfq_service";
import Footer from "../Footer/Footer";
import "./GetQuote.css";

const GetQuote = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const product = location.state || {};
  const user = useMemo(() => LoginService.getCurrentUser(), []); // Memoize user to avoid repeated calls

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      customerName: "",
      customerEmail: "",
      companyName: "",
      mobileNumber: "",
      address: "",
      catalogNumber: "",
      productName: "",
      chemicalName: "",
      casNumber: "",
      molecularNo: "",
      quantity: "",
      uom: "MG",
    },
  });

  const [loading, setLoading] = useState(false);

  // Debounce quantity input changes
  const handleQuantityChange = debounce((value, setValue) => {
    let cleanedValue = value
      .replace(/[^0-9.]/g, "") // Allow numbers and decimal only
      .replace(/(\..*?)\./g, "$1") // Allow only one decimal point
      .replace(/^0+(?=\d)/, ""); // Remove leading zeros
    setValue("quantity", cleanedValue, { shouldValidate: false }); // Validate on blur instead
  }, 300);

  useEffect(() => {
    // Pre-fill user details
    if (user) {
      console.log("User Data:", user);
      setValue("customerName", `${user.firstName || ""} ${user.lastName || ""}`.trim());
      setValue("customerEmail", user.email || "");
      setValue("companyName", user.company || "");
      setValue("mobileNumber", user.mobile || "");
      setValue("address", "");
    }
  }, [user, setValue]);

  useEffect(() => {
    // Pre-fill product details
    if (Object.keys(product).length > 0) {
      console.log("Product Data:", product);
      setValue("catalogNumber", product.catalogNumber || "");
      setValue("productName", product.name || "");
      setValue("chemicalName", product.iupacName || "");
      setValue("casNumber", product.casNumber || "");
      setValue("molecularNo", product.molecularFormula || "");
      setValue("quantity", "");
      setValue("uom", "MG");
    }
  }, [product, setValue]);

  const onSubmit = async (data) => {
    if (!user) {
      if (!data.customerEmail) {
        toast.error("❌ Email is required to create a new account or log in.", {
          autoClose: 6000,
        });
        return;
      }
      toast.success(
        `✅ Account creation requested with ${data.customerEmail}. Please verify your email.`,
        { autoClose: 6000 }
      );
      setTimeout(() => navigate("/login", { replace: false }), 1000);
      return;
    }

    if (data.customerEmail !== user.email) {
      toast.error("❌ You can only submit quotes with your logged-in email.", {
        autoClose: 6000,
      });
      return;
    }

    setLoading(true);

    try {
      const payload = {
        customerName: data.customerName,
        companyName: data.companyName,
        customerEmail: data.customerEmail,
        mobileNumber: data.mobileNumber,
        address: data.address,
        products: [
          {
            catalogNumber: data.catalogNumber,
            productName: data.productName,
            chemicalName: data.chemicalName,
            casNumber: data.casNumber,
            molecularNo: data.molecularNo,
            quantity: data.quantity,
            uom: data.uom,
          },
        ],
      };

      const response = await RfqService.postRfq(payload);
      console.log("Submit RFQ Response:", response);

      if (response.success) {
        toast.success(`✅ Quote submitted successfully!`, { autoClose: 6000 });
        reset();
        setTimeout(() => navigate("/", { replace: false }), 1000);
      } else {
        throw new Error(response.message || "Failed to submit quote");
      }
    } catch (error) {
      toast.error(`❌ ${error.message || "Failed to submit quote. Please try again later."}`, {
        autoClose: 6000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNavigateToModule = () => {
    try {
      navigate("/other-module", { replace: false });
    } catch (error) {
      console.error("Navigation error:", error);
      toast.error("❌ Failed to navigate to the module. Please try again.", {
        autoClose: 6000,
      });
    }
  };

  return (
    <div>
      <div className="quote-form-container">
        <h2 className="form-title">Get Online Quote</h2>

        <form onSubmit={handleSubmit(onSubmit)} className="quote-form">
          <div className="form-section">
            <h3>🧍 Personal Details</h3>

            <label>
              <span className="text-style">Full Name</span>{" "}
              <span className="required-asterisk">*</span>
              <input
                {...register("customerName", { required: "This field is required" })}
                readOnly={!!user} // Make read-only when user is logged in
              />
              {errors.customerName && (
                <span className="error-text">{errors.customerName.message}</span>
              )}
            </label>

            <label>
              Official Email Id<span className="required-asterisk">*</span>
              <input
                type="email"
                {...register("customerEmail", {
                  required: "Email is required",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Invalid email format",
                  },
                })}
                readOnly={!!user} // Make read-only when user is logged in
              />
              {errors.customerEmail && (
                <span className="error-text">{errors.customerEmail.message}</span>
              )}
            </label>

            <label>
              Company Name<span className="required-asterisk">*</span>
              <input
                {...register("companyName", { required: "This field is required" })}
                readOnly={!!user} // Make read-only when user is logged in
              />
              {errors.companyName && (
                <span className="error-text">{errors.companyName.message}</span>
              )}
            </label>

            <label>
              Phone Number<span className="required-asterisk">*</span>
              <input
                type="text"
                maxLength="10"
                inputMode="numeric"
                {...register("mobileNumber", {
                  required: "Contact number is required",
                  pattern: {
                    value: /^[0-9]{10}$/,
                    message: "Enter a valid 10-digit number",
                  },
                })}
                readOnly={!!user} // Make read-only when user is logged in
                onInput={(e) => {
                  if (!user) {
                    e.target.value = e.target.value.replace(/\D/g, "").slice(0, 10);
                  }
                }}
              />
              {errors.mobileNumber && (
                <span className="error-text">{errors.mobileNumber.message}</span>
              )}
            </label>

            <label>
              Address
              <textarea
                {...register("address")}
                rows={3}
                className="address-textarea"
                // Remove readOnly={!!user} to make Address editable for all users
              />
            </label>
          </div>

          <div className="form-section">
            <h3>🧪 Product Details</h3>

            <label>
              Catalog Number
              <input {...register("catalogNumber")} />
            </label>

            <label>
              Product Name<span className="required-asterisk">*</span>
              <input
                {...register("productName", { required: "This field is required" })}
              />
              {errors.productName && (
                <span className="error-text">{errors.productName.message}</span>
              )}
            </label>

            <label>
              Chemical Name
              <input {...register("chemicalName")} />
            </label>

            <label>
              CAS No<span className="required-asterisk">*</span>
              <input
                {...register("casNumber", {
                  required: "CAS No is required",
                  pattern: {
                    value: /^[0-9-]+$/,
                    message: "Enter a valid CAS number (e.g., 123-45-6)",
                  },
                })}
              />
              {errors.casNumber && (
                <span className="error-text">{errors.casNumber.message}</span>
              )}
            </label>

            <label className="molecular-no-label">
              Molecular No
              <input {...register("molecularNo")} />
            </label>

            <label>
              Quantity<span className="required-asterisk">*</span>
              <div className="quantity-row">
                <input
                  type="text"
                  {...register("quantity", {
                    required: "Quantity is required",
                    onBlur: (e) => {
                      const value = e.target.value;
                      if (!/^[0-9]*\.?[0-9]*$/.test(value)) {
                        setError("quantity", {
                          type: "pattern",
                          message: "Enter a valid number (e.g., 0.0001 or 1)",
                        });
                      } else if (parseFloat(value) < 0.0001) {
                        setError("quantity", {
                          type: "validate",
                          message: "Quantity must be at least 0.0001",
                        });
                      } else {
                        clearErrors("quantity");
                      }
                    },
                  })}
                  onChange={(e) => handleQuantityChange(e.target.value, setValue)}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                      e.preventDefault();
                      const currentValue = parseFloat(e.target.value) || 0;
                      const step = 0.0001;
                      let newValue =
                        e.key === "ArrowUp"
                          ? currentValue + 1
                          : Math.max(currentValue - step, 0.0001);
                      e.target.value = newValue.toFixed(4);
                      setValue("quantity", newValue.toFixed(4), { shouldValidate: false });
                    }
                  }}
                />
                <select {...register("uom")}>
                  <option value="MG">MG</option>
                  <option value="G">G</option>
                  <option value="KG">KG</option>
                  <option value="LPM">LPM</option>
                  <option value="PPM">PPM</option>
                  <option value="ML">ML</option>
                  <option value="L">L</option>
                </select>
              </div>
              {errors.quantity && (
                <span className="error-text">{errors.quantity.message}</span>
              )}
            </label>

            <button className="submit-btn12" type="submit" disabled={loading}>
              {loading ? "Submit..." : "Submit"}
            </button>
          </div>
        </form>
      </div>

      <Footer />

      <ToastContainer
        position="bottom-center"
        autoClose={6000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default GetQuote;